import { type CSSProperties, type HTMLAttributes, type KeyboardEvent, type ReactElement, type ReactNode, type Ref, useId, useState } from 'react';
import { MAP_MARKER_BOX, MAP_MARKER_HEAD_BOX, mapMarkerAnchor, mapMarkerDataUri, mapMarkerHeight, type MapMarkerKind } from '@vello/brand';
import { useColorScheme } from '../theme-preference';

/**
 * D550 (ADMIN-A): the pieces of a tiled map stage (the Admin's live map and risk-area editor). The stage's geometry is DATA — the
 * map height, a tile's pixel position, a marker's pixel position and colour — so each piece takes it as a prop and writes it as a
 * CSS custom property on its own element; every other measure (size, border, radius, stacking, the 48px marker box) is the
 * stylesheet's. A screen composes these and sets no `style`.
 */

type Vars = CSSProperties & Record<`--${string}`, string | number | undefined>;

export interface MapStageProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'className'> {
  /** The stage height in px (the viewport the tile maths runs in). */
  readonly height: number;
  readonly ref?: Ref<HTMLDivElement>;
  readonly children?: ReactNode;
}

/** The map viewport: a clipped, sunken box the tiles, overlays, markers and controls are placed in. Pass the pointer handlers through. */
export function MapStage({ height, ref, children, ...rest }: MapStageProps): ReactElement {
  const vars: Vars = { '--vello-map-h': `${height}px` };
  return (
    <div {...rest} ref={ref} className="vello-admin-a-map" style={vars}>
      {children}
    </div>
  );
}

/** One 256px raster tile at a pixel offset inside the stage. */
export function MapTile({ src, left, top }: { readonly src: string; readonly left: number; readonly top: number }): ReactElement {
  const vars: Vars = { '--vello-x': `${left}px`, '--vello-y': `${top}px` };
  return <img className="vello-admin-a-map__tile" src={src} alt="" draggable={false} referrerPolicy="no-referrer" style={vars} />;
}

/** A full-stage SVG layer for areas and routes (it never takes pointer events). */
export function MapOverlay({ width, height, children }: { readonly width: number; readonly height: number; readonly children: ReactNode }): ReactElement {
  return (
    <svg className="vello-admin-a-map__overlay" width={width} height={height} aria-hidden="true">
      {children}
    </svg>
  );
}

export interface MapMarkerProps {
  readonly left: number;
  readonly top: number;
  /** The marker colour as a token reference (`var(--color-…)`). */
  readonly color: string;
  /** `square` for a ride, `round` for a driver or a store. */
  readonly shape: 'square' | 'round';
  /** A stale position is drawn as an outline only. */
  readonly stale?: boolean;
  readonly selected?: boolean;
  /** Clickable (pointer cursor); otherwise it is only a mark. */
  readonly interactive?: boolean;
  readonly label: string;
  readonly onClick?: () => void;
  /**
   * The marker's details, shown as a tooltip bubble on hover AND on keyboard focus (Escape dismisses it). Phrasing content only
   * (spans: the bubble lives inside the marker's button); use {@link MapMarkerTipLine} for each line. The marker is described by it.
   */
  readonly tip?: ReactNode;
  /** Where the bubble sits: above the marker (default) or below it (a marker near the stage's top edge). */
  readonly tipPlacement?: 'top' | 'bottom';
  /** Horizontal anchor of the bubble: centred (default), or flush to the marker's start / end (a marker near a side edge). */
  readonly tipAlign?: 'center' | 'start' | 'end';
}

/** One line of a marker's tip. */
export function MapMarkerTipLine({ children }: { readonly children: ReactNode }): ReactElement {
  return <span className="vello-admin-a-marker__tip-line">{children}</span>;
}

/** A marker: a 14/18px dot centred in a 48px button box, so it keeps the hit area whatever the dot size. */
export function MapMarker({ left, top, color, shape, stale = false, selected = false, interactive = false, label, onClick, tip, tipPlacement = 'top', tipAlign = 'center' }: MapMarkerProps): ReactElement {
  const vars: Vars = { '--vello-x': `${left}px`, '--vello-y': `${top}px`, '--vello-marker': color };
  const tipId = useId();
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const open = tip !== undefined && (hover || focus) && !dismissed;
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Escape') setDismissed(true);
  };
  return (
    <button
      type="button"
      className="vello-admin-a-marker"
      aria-label={label}
      aria-pressed={selected}
      aria-describedby={tip !== undefined ? tipId : undefined}
      onClick={onClick}
      onMouseEnter={tip !== undefined ? () => { setHover(true); setDismissed(false); } : undefined}
      onMouseLeave={tip !== undefined ? () => setHover(false) : undefined}
      onFocus={tip !== undefined ? () => { setFocus(true); setDismissed(false); } : undefined}
      onBlur={tip !== undefined ? () => setFocus(false) : undefined}
      onKeyDown={tip !== undefined ? onKeyDown : undefined}
      data-interactive={interactive ? 'true' : undefined}
      data-tip-open={open ? 'true' : undefined}
      style={vars}
    >
      <span aria-hidden="true" className="vello-admin-a-marker__dot" data-shape={shape} data-stale={stale ? 'true' : undefined} data-selected={selected ? 'true' : undefined} />
      {tip !== undefined && (
        <span id={tipId} role="tooltip" className="vello-tooltip vello-admin-a-marker__tip" data-placement={tipPlacement} data-align={tipAlign} data-open={open ? 'true' : 'false'}>
          <span className="vello-tooltip__bubble">{tip}</span>
        </span>
      )}
    </button>
  );
}

/** The zoom / fit controls, stacked at the stage's top-right corner. */
export function MapControls({ children }: { readonly children: ReactNode }): ReactElement {
  return <div className="vello-admin-a-map__controls">{children}</div>;
}

/** The tile provider's attribution line, at the stage's bottom-left corner. */
export function MapAttribution({ children }: { readonly children: ReactNode }): ReactElement {
  return <div className="vello-admin-a-map__attribution">{children}</div>;
}

/**
 * D800: a ride's end on a web map — the Design System's lollipop ("Marcadores no mapa": the pickup disc with its green core, the
 * chequered destination, the numbered stop), the base of its stem on the point (`left` / `top`), light or dark with the
 * console. Decorative: the map's legend and lists carry the words.
 */
export function MapRouteMarker({ kind, left, top, n }: { readonly kind: MapMarkerKind; readonly left: number; readonly top: number; readonly n?: number }): ReactElement {
  const scheme = useColorScheme();
  const height = mapMarkerHeight(kind);
  const box = MAP_MARKER_BOX[kind];
  const vars: Vars = { '--vello-x': `${left}px`, '--vello-y': `${top}px`, '--vello-w': `${(height * box.width) / box.height}px`, '--vello-h': `${height}px`, '--vello-ay': mapMarkerAnchor(kind).y };
  return <img className="vello-route-marker" src={mapMarkerDataUri(kind, scheme, n !== undefined ? { n } : {})} alt="" aria-hidden="true" draggable={false} style={vars} />;
}

/** D800: the bare marker (no stem) that starts a route row in a list — the same art as the map's, light or dark with the console. */
export function RouteMarkerIcon({ kind, n, size = 18 }: { readonly kind: MapMarkerKind; readonly n?: number; readonly size?: number }): ReactElement {
  const scheme = useColorScheme();
  const box = MAP_MARKER_HEAD_BOX[kind];
  const vars: Vars = { '--vello-w': `${(size * box.width) / box.height}px`, '--vello-h': `${size}px` };
  return <img className="vello-route-marker-icon" src={mapMarkerDataUri(kind, scheme, { head: true, ...(n !== undefined ? { n } : {}) })} alt="" aria-hidden="true" style={vars} />;
}
